import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import { BoardAction } from "../dtos";
import { displayFileName } from "../utils/file-name";
import { primaryButtonSx } from "./field-shell";

/**
 * Full-width banner above the loan process (mock: blue "Next action" / red "Blocking action"
 * + an "Upcoming: X, Y, +N" summary and a Review button opening the upcoming-actions modal).
 */
/** §EditsNeeded: "KYC, Credit Documents & Payment Plan" — design joins with commas and a final &. */
const joinTitles = (titles: string[]): string =>
  titles.length <= 1
    ? (titles[0] ?? "")
    : `${titles.slice(0, -1).join(", ")} & ${titles[titles.length - 1]}`;

const NextActionBanner = ({
  nextAction,
  nextAgentAction = null,
  upcomingActions,
  editsTitles = [],
  editsItems = [],
  conditionalTitles = [],
  conditionalItems = [],
  blockedStepName,
  currentStepName,
  onReview,
}: {
  nextAction: BoardAction | null;
  /** §NextForAgent: the step that WILL land on the agent when nothing is open for them now */
  nextAgentAction?: BoardAction | null;
  upcomingActions: BoardAction[];
  /** §EditsNeeded: every sent-back step — the red banner reads "Update KYC, credit documents & payment plan" */
  editsTitles?: string[];
  /** §EditsNeeded: WHAT is owed — rejected file names / field labels, shown as the detail segment */
  editsItems?: string[];
  /** §Conditional: steps carrying approved-with-conditions files — orange banner variant */
  conditionalTitles?: string[];
  /** §Conditional: the conditional file names, shown as the detail segment */
  conditionalItems?: string[];
  /** a rejected step dead-ends the flow — the banner must say so, not "pending other parties" */
  blockedStepName?: string | null;
  /** §37: the flow's current in-progress step — named when nothing is agent-actionable */
  currentStepName?: string | null;
  onReview: () => void;
}) => {
  const theme = useTheme();
  const layer = theme.nawy.colors.semantic.layer;
  const text = theme.nawy.colors.semantic.text;
  const border = theme.nawy.colors.semantic.border;

  if (!nextAction && !nextAgentAction && upcomingActions.length === 0 && !blockedStepName)
    return null;

  // a true rejection (dead end) outranks the edits treatment; both wear the red ramp.
  // §Conditional wears the ORANGE ramp and yields to both red states.
  // §Reopened (product ask, 2026-08-06): a reopened next step is red too — it came BACK.
  const isBlocking = nextAction?.kind === "BLOCKING" || (!nextAction && !!blockedStepName);
  const isEdits =
    !isBlocking &&
    (nextAction?.kind === "EDITS" || editsTitles.length > 0 || Boolean(nextAction?.reopened));
  const isConditionalBanner =
    !isBlocking &&
    !isEdits &&
    (nextAction?.kind === "CONDITIONAL" || conditionalTitles.length > 0);
  const upcomingSummary = upcomingActions
    .slice(0, 2)
    .map((action) => action.title)
    .join(", ");
  const upcomingExtra =
    upcomingActions.length > 2 ? `, +${upcomingActions.length - 2}` : "";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        // §Mobile (design "[Mobile] Alert"): the design's banner is ONE 60px row — the action and
        // its CTA, nothing else. The detail segments below are hidden on a phone (they are all in
        // the Review sheet one tap away), so the row no longer needs to wrap and the banner stops
        // eating a third of the screen. From tablet up every segment shows and wrapping still
        // guards the button (flex children need minWidth: 0 or their intrinsic width beats it).
        flexWrap: { mobile: "nowrap", tablet: "wrap" },
        rowGap: 1,
        gap: { mobile: 1.5, tablet: 2 },
        borderRadius: theme.nawy.radius.xl,
        // §Banner-border: every surface in the design carries a tone-matched hairline — the
        // start-flow banner's is `0.5px` warning on warning-light (nawy-now-lead-banner). This one
        // was the odd surface out: a tinted fill floating with no edge. Same recipe per tone,
        // from the semantic tokens (negative = #C72D22, accent = #015C9A).
        border: `0.5px solid ${
          isBlocking || isEdits
            ? border.extension.negative
            : isConditionalBanner
              ? "#F76700"
              : border.accent
        }`,
        backgroundColor:
          isBlocking || isEdits
            ? layer.extension.negativeLight
            : isConditionalBanner
              ? "#FFF6F0"
              : layer.accentLight,
        px: 2,
        py: 1.5,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          fontSize={11}
          lineHeight="16px"
          color={text.secondary}
        >
          {nextAction
            ? isBlocking
              ? "Blocking action"
              : "Next action"
            : blockedStepName
              ? "Flow blocked"
              : nextAgentAction
                ? "Up next for you"
                : currentStepName
                  ? "Next action"
                  : "No next action"}
        </Typography>
        <Typography
          fontSize={14}
          lineHeight="20px"
          fontWeight={600}
          color={
            isBlocking || isEdits
              ? text.extension.negative
              : isConditionalBanner
                ? "#E65F00"
                : text.accent
          }
        >
          {/* §EditsNeeded (design): every sent-back step in one breath — "Update KYC, credit
              documents & payment plan" — not just the first one */}
          {isEdits && editsTitles.length > 0
            ? `Update ${joinTitles(editsTitles)}`
            : isConditionalBanner && conditionalTitles.length > 0
              ? `Review conditions on ${joinTitles(conditionalTitles)}`
              : ((nextAction &&
                  (nextAction.reopened ? `${nextAction.title} — reopened` : nextAction.title)) ??
              (blockedStepName
                ? `${blockedStepName} rejected`
                : /* §NextForAgent: nothing open for the agent — name what WILL be theirs,
                     with the flow's current step as context when another party holds it */
                  nextAgentAction
                  ? currentStepName && currentStepName !== nextAgentAction.title
                    ? `${nextAgentAction.title} — after ${currentStepName}`
                    : nextAgentAction.title
                  : currentStepName
                    ? `${currentStepName} — pending other parties`
                    : "Pending other parties"))}
        </Typography>
      </Box>

      {/* §EditsNeeded: the DETAIL — which document/field was rejected, not just the step. Shown
          beside a Blocking-action banner too: a flow blocker doesn't cancel the rejected-file debt. */}
      {/* §Conditional: the detail — which files carry conditions (orange) */}
      {conditionalItems.length > 0 && (
        <Box
          sx={{
            // §Mobile: detail segments live in the Review sheet on a phone — see the container note
            display: { mobile: "none", tablet: "block" },
            borderLeft: `1px solid ${theme.nawy.colors.semantic.border.secondary}`,
            pl: 2,
            minWidth: 0,
            maxWidth: "100%",
          }}
        >
          <Typography fontSize={11} lineHeight="16px" color={text.secondary}>
            Conditions
          </Typography>
          <Typography fontSize={14} lineHeight="20px" fontWeight={600} sx={{ color: "#E65F00" }} noWrap>
            {conditionalItems.slice(0, 2).map(displayFileName).join(", ")}
            {conditionalItems.length > 2 ? `, +${conditionalItems.length - 2}` : ""}
          </Typography>
        </Box>
      )}

      {(isEdits || isBlocking) && editsItems.length > 0 && (
        <Box
          sx={{
            // §Mobile: detail segments live in the Review sheet on a phone — see the container note
            display: { mobile: "none", tablet: "block" },
            borderLeft: `1px solid ${theme.nawy.colors.semantic.border.secondary}`,
            pl: 2,
            minWidth: 0,
            maxWidth: "100%",
          }}
        >
          <Typography fontSize={11} lineHeight="16px" color={text.secondary}>
            Needs fixing
          </Typography>
          <Typography
            fontSize={14}
            lineHeight="20px"
            fontWeight={600}
            color={text.primary}
            noWrap
          >
            {editsItems.slice(0, 2).map(displayFileName).join(", ")}
            {editsItems.length > 2 ? `, +${editsItems.length - 2}` : ""}
          </Typography>
        </Box>
      )}

      {upcomingActions.length > 0 && (
        <Box
          sx={{
            // §Mobile: detail segments live in the Review sheet on a phone — see the container note
            display: { mobile: "none", tablet: "block" },
            borderLeft: `1px solid ${theme.nawy.colors.semantic.border.secondary}`,
            pl: 2,
            minWidth: 0,
            maxWidth: "100%",
          }}
        >
          <Typography fontSize={11} lineHeight="16px" color={text.secondary}>
            Upcoming
          </Typography>
          <Typography
            fontSize={14}
            lineHeight="20px"
            fontWeight={600}
            color={text.primary}
            noWrap
          >
            {upcomingSummary}
            {upcomingExtra}
          </Typography>
        </Box>
      )}

      <Stack
        direction="row"
        justifyContent="flex-end"
        sx={{ flex: 1, flexShrink: 0, minWidth: "auto" }}
      >
        {/* §MobileQA: the colours were already brand, but with no height/line-height MUI's own
            button metrics applied — 37px tall at 14/25 where every button in the design is
            32px at Body/md - Semibold 14/20. */}
        <Button
          variant="contained"
          disableElevation
          onClick={onReview}
          sx={{
            ...primaryButtonSx(theme),
            textTransform: "none",
            "&:hover": { boxShadow: "none", backgroundColor: layer.accentDark },
          }}
        >
          Review
        </Button>
      </Stack>
    </Box>
  );
};

export default NextActionBanner;
